import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import SEO from '../components/common/SEO';
import LoadingSpinner from '../components/common/LoadingSpinner';
import {
  usersAPI, lessonsAPI, categoriesAPI, tipsAPI, storiesAPI, publicAPI,
  pageContentAPI, landingAnimationAPI,
} from '../services/api';

const TABS = ['stats', 'pageContent', 'animations', 'lessons', 'categories', 'tips', 'stories', 'users', 'messages'];

const PAGES = ['landing', 'about', 'learn', 'tips', 'stories', 'contact'];
const ANIM_TYPES = ['float', 'pulse', 'orbit', 'shimmer', 'wave', 'bounce', 'glow', 'particle'];

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      if (tab === 'stats') {
        const res = await usersAPI.getStats();
        setStats(res.data.data);
      } else if (tab === 'pageContent') {
        const res = await pageContentAPI.getAll({ limit: 100 });
        setData(res.data.data);
      } else if (tab === 'animations') {
        const res = await landingAnimationAPI.getAll();
        setData(res.data.data);
      } else if (tab === 'lessons') {
        const res = await lessonsAPI.getAll({ limit: 50 });
        setData(res.data.data);
      } else if (tab === 'categories') {
        const res = await categoriesAPI.getAll({ limit: 50 });
        setData(res.data.data);
      } else if (tab === 'tips') {
        const res = await tipsAPI.getAll({ limit: 50 });
        setData(res.data.data);
      } else if (tab === 'stories') {
        const res = await storiesAPI.getAll({ limit: 50 });
        setData(res.data.data);
      } else if (tab === 'users') {
        const res = await usersAPI.getAll({ limit: 50 });
        setData(res.data.data);
      } else if (tab === 'messages') {
        const res = await publicAPI.getMessages();
        setData(res.data.data);
      }
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [tab]);

  const handleCreate = async () => {
    try {
      if (tab === 'pageContent') {
        const bullets = form.bulletPoints
          ? form.bulletPoints.split('\n').map((b) => b.trim()).filter(Boolean)
          : [];
        await pageContentAPI.create({ ...form, bulletPoints: bullets, language: form.language || 'en' });
      } else if (tab === 'animations') {
        await landingAnimationAPI.create(form);
      } else if (tab === 'categories') await categoriesAPI.create(form);
      else if (tab === 'tips') await tipsAPI.create(form);
      else if (tab === 'lessons') await lessonsAPI.create(form);
      else if (tab === 'stories') await storiesAPI.create(form);
      toast.success('Created!');
      setForm({});
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Create failed');
    }
  };

  const handleToggleAnimation = async (item) => {
    try {
      await landingAnimationAPI.update(item.id, { isEnabled: !item.isEnabled });
      toast.success(item.isEnabled ? 'Animation disabled' : 'Animation enabled');
      fetchData();
    } catch {
      toast.error('Update failed');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this item?')) return;
    try {
      if (tab === 'pageContent') await pageContentAPI.delete(id);
      else if (tab === 'animations') await landingAnimationAPI.delete(id);
      else if (tab === 'lessons') await lessonsAPI.delete(id);
      else if (tab === 'categories') await categoriesAPI.delete(id);
      else if (tab === 'tips') await tipsAPI.delete(id);
      else if (tab === 'stories') await storiesAPI.delete(id);
      else if (tab === 'users') await usersAPI.delete(id);
      else if (tab === 'messages') await publicAPI.deleteMessage(id);
      toast.success('Deleted');
      fetchData();
    } catch {
      toast.error('Delete failed');
    }
  };

  const tabLabels = {
    stats: t('dashboard.stats'),
    pageContent: 'Page Content',
    animations: 'Landing Animations',
    lessons: t('dashboard.manageLessons'),
    categories: t('dashboard.manageCategories'),
    tips: t('dashboard.manageTips'),
    stories: t('dashboard.manageStories'),
    users: t('dashboard.manageUsers'),
    messages: t('dashboard.messages'),
  };

  return (
    <>
      <SEO title={t('dashboard.admin')} />
      <section className="py-8 px-4 max-w-6xl mx-auto">
        <h1 className="section-title mb-6">{t('dashboard.admin')}</h1>

        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((tb) => (
            <button key={tb} onClick={() => setTab(tb)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === tb ? 'bg-brand-600 text-white' : 'bg-gray-100 dark:bg-gray-800'}`}>
              {tabLabels[tb]}
            </button>
          ))}
        </div>

        {loading ? <LoadingSpinner className="py-20" /> : (
          <>
            {tab === 'stats' && stats && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(stats).map(([key, val]) => (
                  <div key={key} className="card text-center">
                    <p className="text-3xl font-bold text-brand-600">{val}</p>
                    <p className="text-sm text-gray-500 capitalize mt-1">{key}</p>
                  </div>
                ))}
              </div>
            )}

            {tab === 'pageContent' && (
              <div className="card mb-6 space-y-3">
                <h3 className="font-bold">Add Page Content Block</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  <select className="input-field" value={form.page || ''} onChange={(e) => setForm({ ...form, page: e.target.value })}>
                    <option value="">Select Page</option>
                    {PAGES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <input className="input-field" placeholder="Section Key (e.g. why_us)" value={form.sectionKey || ''} onChange={(e) => setForm({ ...form, sectionKey: e.target.value })} />
                  <input className="input-field" placeholder="Title" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                  <input className="input-field" placeholder="Subtitle" value={form.subtitle || ''} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
                  <input className="input-field" placeholder="Icon (emoji)" value={form.icon || ''} onChange={(e) => setForm({ ...form, icon: e.target.value })} />
                  <select className="input-field" value={form.language || 'en'} onChange={(e) => setForm({ ...form, language: e.target.value })}>
                    <option value="en">English</option>
                    <option value="mr">Marathi</option>
                  </select>
                </div>
                <textarea className="input-field" placeholder="Content paragraph" value={form.content || ''} onChange={(e) => setForm({ ...form, content: e.target.value })} />
                <textarea className="input-field" placeholder="Bullet points (one per line)" value={form.bulletPoints || ''} onChange={(e) => setForm({ ...form, bulletPoints: e.target.value })} />
                <button onClick={handleCreate} className="btn-primary">{t('common.save')}</button>
              </div>
            )}

            {tab === 'animations' && (
              <div className="card mb-6 space-y-3">
                <h3 className="font-bold">Add Landing Animation</h3>
                <div className="grid md:grid-cols-3 gap-3">
                  <input className="input-field" placeholder="Name" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <select className="input-field" value={form.animationType || ''} onChange={(e) => setForm({ ...form, animationType: e.target.value })}>
                    <option value="">Animation Type</option>
                    {ANIM_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                  <input className="input-field" placeholder="Emoji" value={form.emoji || '💰'} onChange={(e) => setForm({ ...form, emoji: e.target.value })} />
                  <input className="input-field" placeholder="Color (#hex)" value={form.color || '#14b8a6'} onChange={(e) => setForm({ ...form, color: e.target.value })} />
                  <select className="input-field" value={form.size || 'md'} onChange={(e) => setForm({ ...form, size: e.target.value })}>
                    <option value="sm">Small</option><option value="md">Medium</option><option value="lg">Large</option><option value="xl">XL</option>
                  </select>
                  <select className="input-field" value={form.speed || 'normal'} onChange={(e) => setForm({ ...form, speed: e.target.value })}>
                    <option value="slow">Slow</option><option value="normal">Normal</option><option value="fast">Fast</option>
                  </select>
                  <input className="input-field" placeholder="Position X (e.g. 10%)" value={form.positionX || '50%'} onChange={(e) => setForm({ ...form, positionX: e.target.value })} />
                  <input className="input-field" placeholder="Position Y (e.g. 20%)" value={form.positionY || '50%'} onChange={(e) => setForm({ ...form, positionY: e.target.value })} />
                </div>
                <button onClick={handleCreate} className="btn-primary">{t('common.save')}</button>
              </div>
            )}

            {['categories', 'tips', 'lessons', 'stories'].includes(tab) && (
              <div className="card mb-6 space-y-3">
                <h3 className="font-bold">{t('common.add')}</h3>
                {tab === 'categories' && (
                  <>
                    <input className="input-field" placeholder="Title" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value, language: 'en' })} />
                    <input className="input-field" placeholder="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    <input className="input-field" placeholder="Icon (emoji)" value={form.icon || ''} onChange={(e) => setForm({ ...form, icon: e.target.value })} />
                  </>
                )}
                {tab === 'tips' && (
                  <>
                    <input className="input-field" placeholder="Title" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value, language: 'en' })} />
                    <textarea className="input-field" placeholder="Content" value={form.content || ''} onChange={(e) => setForm({ ...form, content: e.target.value })} />
                  </>
                )}
                {tab === 'lessons' && (
                  <>
                    <input className="input-field" placeholder="Title" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                    <input className="input-field" placeholder="Category ID" type="number" value={form.categoryId || ''} onChange={(e) => setForm({ ...form, categoryId: +e.target.value, language: 'en', fullContent: form.fullContent || 'Content here' })} />
                    <textarea className="input-field" placeholder="Full Content" value={form.fullContent || ''} onChange={(e) => setForm({ ...form, fullContent: e.target.value })} />
                  </>
                )}
                {tab === 'stories' && (
                  <>
                    <input className="input-field" placeholder="Title" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value, language: 'en', authorName: form.authorName || 'Author' })} />
                    <textarea className="input-field" placeholder="Story" value={form.story || ''} onChange={(e) => setForm({ ...form, story: e.target.value })} />
                    <input className="input-field" placeholder="Author Name" value={form.authorName || ''} onChange={(e) => setForm({ ...form, authorName: e.target.value })} />
                  </>
                )}
                <button onClick={handleCreate} className="btn-primary">{t('common.save')}</button>
              </div>
            )}

            {tab !== 'stats' && (
              <div className="space-y-3">
                {data.map((item) => (
                  <div key={item.id} className="card flex justify-between items-center gap-4">
                    <div className="flex-1 min-w-0">
                      {tab === 'pageContent' && (
                        <>
                          <p className="font-semibold">{item.icon} {item.title}</p>
                          <p className="text-sm text-gray-500">{item.page} · {item.sectionKey} · {item.language}</p>
                        </>
                      )}
                      {tab === 'animations' && (
                        <>
                          <p className="font-semibold">{item.emoji} {item.name} — {item.animationType}</p>
                          <p className="text-sm text-gray-500">{item.positionX}, {item.positionY} · {item.isEnabled ? '✅ Enabled' : '❌ Disabled'}</p>
                        </>
                      )}
                      {!['pageContent', 'animations'].includes(tab) && (
                        <>
                          <p className="font-semibold">{item.title || item.name || item.email || item.subject}</p>
                          <p className="text-sm text-gray-500 truncate">{item.email || item.shortDescription || item.message?.slice(0, 60)}</p>
                        </>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {tab === 'animations' && (
                        <button onClick={() => handleToggleAnimation(item)} className="text-brand-600 text-sm hover:underline">
                          {item.isEnabled ? 'Disable' : 'Enable'}
                        </button>
                      )}
                      <button onClick={() => handleDelete(item.id)} className="text-red-500 text-sm hover:underline">{t('common.delete')}</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}
